import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Banknote, Landmark, Smartphone, ArrowLeft, AlertTriangle } from "lucide-react";
import { api, apiError, formatINR } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { UpiPayBlock } from "../components/UpiPayBlock";

const METHODS = [
  { value: "cod", label: "Cash on Delivery", desc: "Pay when your parcel arrives", icon: Banknote },
  { value: "upi", label: "UPI", desc: "GPay, PhonePe, Paytm & more", icon: Smartphone },
  { value: "bank_transfer", label: "Bank Transfer", desc: "NEFT / IMPS to our account", icon: Landmark },
];

export default function CheckoutPage() {
  const { cart, cartTotal, clearCart, settings } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "", pincode: "", note: "" });
  const [method, setMethod] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");

  if (cart.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-28 text-center" data-testid="checkout-empty">
        <Seo title="Checkout — Anaya Fashion & Gifts" path="/checkout" noindex />
        <h1 className="font-display text-4xl">Nothing to check out</h1>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-foreground px-7 py-3 text-xs tracking-[0.2em] text-white uppercase hover:bg-primary">Shop first</Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) return setError("Please enter your full name.");
    if (form.phone.replace(/\D/g, "").length < 10) return setError("Please enter a valid 10-digit phone number.");
    if (form.address.trim().length < 8) return setError("Please enter your complete delivery address.");
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        customer: form,
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty, variant: i.variant || "" })),
        payment_method: method,
      });
      localStorage.setItem(`anaya_order_${data.order_reference}`, JSON.stringify(data));
      clearCart();
      toast.success("Order placed!");
      navigate(`/order/${data.order_reference}`);
    } catch (err) {
      setError(apiError(err, "Could not place your order. Please try again."));
    } finally {
      setPlacing(false);
    }
  };

  const enabled = { cod: settings?.cod_enabled !== false, upi: settings?.upi_enabled !== false, bank_transfer: settings?.bank_enabled !== false };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="checkout-page">
      <Seo title="Checkout — Anaya Fashion & Gifts" path="/checkout" noindex />
      <Reveal>
        <Link to="/cart" className="flex items-center gap-1 text-xs tracking-[0.2em] text-muted-foreground uppercase hover:text-primary" data-testid="back-to-cart">
          <ArrowLeft size={13} /> Back to bag
        </Link>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl">Checkout</h1>
        <p className="mt-2 text-sm font-light text-muted-foreground">No account needed — just your details and a payment choice.</p>
      </Reveal>

      <form onSubmit={placeOrder} className="mt-10 grid gap-10 lg:grid-cols-[1fr_320px]">
        <div className="space-y-8">
          <Reveal>
            <div className="glass rounded-3xl p-6 sm:p-8">
              <p className="font-display text-xl">Delivery details</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Full name *</span>
                  <input data-testid="checkout-name" required value={form.name} onChange={set("name")}
                    className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="Your name" />
                </label>
                <label className="block">
                  <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Phone *</span>
                  <input data-testid="checkout-phone" required value={form.phone} onChange={set("phone")} type="tel"
                    className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="10-digit mobile" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Full address *</span>
                  <textarea data-testid="checkout-address" required value={form.address} onChange={set("address")} rows={3}
                    className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="House, street, landmark" />
                </label>
                <label className="block">
                  <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">City</span>
                  <input data-testid="checkout-city" value={form.city} onChange={set("city")}
                    className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="City" />
                </label>
                <label className="block">
                  <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Pincode</span>
                  <input data-testid="checkout-pincode" value={form.pincode} onChange={set("pincode")}
                    className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="110001" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Note for us (optional)</span>
                  <input data-testid="checkout-note" value={form.note} onChange={set("note")}
                    className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" placeholder="Gift note, delivery instructions..." />
                </label>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="glass rounded-3xl p-6 sm:p-8">
              <p className="font-display text-xl">Payment</p>
              <div className="mt-5 space-y-3" data-testid="payment-methods">
                {METHODS.filter((m) => enabled[m.value]).map(({ value, label, desc, icon: Icon }) => (
                  <label key={value} data-testid={`payment-${value}`}
                    className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-4 transition-all ${method === value ? "border-primary bg-white/80 shadow-sm" : "border-input bg-white/50 hover:border-primary/40"}`}>
                    <input type="radio" name="payment" value={value} checked={method === value} onChange={() => setMethod(value)} className="accent-primary" />
                    <Icon size={20} className="text-primary" />
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-xs font-light text-muted-foreground">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
              {method === "cod" && (
                <div className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5" data-testid="cod-notice">
                  <p className="flex items-center gap-2 text-sm font-semibold tracking-wide text-amber-800">
                    <AlertTriangle size={16} /> Important: COD orders require advance shipping payment
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-amber-900/80">
                    Want to order with Cash on Delivery? Pay <strong>₹120 shipping charges</strong> in advance to confirm your COD order. The remaining product amount will be payable at the time of delivery.
                  </p>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-amber-900">After placing your COD order:</p>
                  <ol className="mt-1 list-decimal space-y-1 pl-5 text-xs leading-relaxed text-amber-900/80">
                    <li>Receive your unique Order Confirmation Code.</li>
                    <li>Pay ₹120 shipping charges in advance.</li>
                    <li>Send the Order Confirmation Code and payment screenshot to our WhatsApp: <a href={`https://wa.me/${settings?.whatsapp || "919643444088"}`} target="_blank" rel="noreferrer" className="font-semibold underline">9643444088</a>.</li>
                    <li>Our team will verify your payment.</li>
                    <li>Your COD order will be confirmed and dispatched only after successful verification.</li>
                  </ol>
                  <p className="mt-3 rounded-xl bg-amber-100 px-3 py-2 text-[11px] font-semibold uppercase leading-relaxed tracking-wide text-amber-900">
                    Please note: without advance shipping payment and WhatsApp confirmation, your COD order will not be confirmed or dispatched.
                  </p>
                </div>
              )}
              {method === "upi" && (
                <div className="mt-4 rounded-2xl bg-accent/40 p-5 text-sm font-light" data-testid="upi-details">
                  <p className="font-medium tracking-wide text-accent-foreground uppercase text-xs">Complete your payment &amp; confirm your order</p>
                  <UpiPayBlock settings={settings} />
                  <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                    After placing your order, you will receive a unique Order Confirmation Code. Send this code along with your full payment screenshot to our WhatsApp number <a href={`https://wa.me/${settings?.whatsapp || "919643444088"}`} target="_blank" rel="noreferrer" className="font-medium underline">9643444088</a>. Your order will be confirmed and dispatched after successful payment verification.
                  </p>
                </div>
              )}
              {method === "bank_transfer" && (
                <div className="mt-4 rounded-2xl bg-accent/40 p-5 text-sm font-light" data-testid="bank-details">
                  <p className="font-medium tracking-wide text-accent-foreground uppercase text-xs">Make your bank transfer &amp; confirm your order</p>
                  <div className="mt-3 rounded-xl bg-white/80 p-4 text-xs leading-relaxed">
                    <p>Bank Name: <span className="font-medium">{settings?.bank_name}</span></p>
                    <p>Account Number: <span className="font-medium tracking-wider">{settings?.bank_account_number}</span></p>
                    <p>IFSC Code: <span className="font-medium tracking-wider">{settings?.bank_ifsc}</span></p>
                    <p>Account Name: <span className="font-medium">{settings?.bank_account_name}</span></p>
                  </div>
                  <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs leading-relaxed text-muted-foreground">
                    <li>Place your order and receive your unique Order Confirmation Code.</li>
                    <li>Transfer the required payment to the bank account displayed above.</li>
                    <li>Send the Order Confirmation Code and payment screenshot to WhatsApp: <a href={`https://wa.me/${settings?.whatsapp || "919643444088"}`} target="_blank" rel="noreferrer" className="font-medium underline">9643444088</a>.</li>
                    <li>Our team will verify the payment.</li>
                    <li>Your order will be confirmed and dispatched after successful verification.</li>
                  </ol>
                </div>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="glass-deep h-fit rounded-3xl p-6 lg:sticky lg:top-28" data-testid="checkout-summary">
            <p className="font-display text-xl">Your order</p>
            <ul className="mt-4 space-y-2 text-sm font-light">
              {cart.map((i) => (
                <li key={i.id} className="flex justify-between gap-3">
                  <span className="line-clamp-1">{i.name}{i.variant ? ` · Design ${i.variant}` : ""} × {i.qty}</span>
                  <span className="shrink-0">{formatINR(i.price * i.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-2 border-t border-input pt-4 text-sm font-light">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">{formatINR(cartTotal)}</span></div>
              <div className="flex justify-between">
                <span>Shipping ({method === "cod" ? "COD" : "prepaid"})</span>
                <span data-testid="checkout-shipping">{formatINR(method === "cod" ? 120 : 70)}</span>
              </div>
            </div>
            <div className="mt-3 flex justify-between border-t border-input pt-3 font-medium">
              <span>Total payable</span><span data-testid="checkout-total">{formatINR(cartTotal + (method === "cod" ? 120 : 70))}</span>
            </div>
            <div className="mt-4 rounded-2xl border border-primary/30 bg-secondary/50 p-4 text-xs font-light leading-relaxed" data-testid="return-policy-notice">
              <p className="text-[11px] font-semibold uppercase tracking-wide">Return &amp; Refund Policy</p>
              <p className="mt-1">A <strong>clear unboxing video is mandatory</strong> when opening your parcel. Returns or replacements are accepted only for <strong>damaged, defective or incorrect products</strong> with valid unboxing video proof shared on <strong>WhatsApp within 24 hours of delivery</strong>. <strong>No unboxing video, no return or replacement.</strong> No returns for personal preferences or change of mind.</p>
              <Link to="/policies/returns" data-testid="return-policy-link" className="mt-1.5 inline-block font-medium text-primary underline underline-offset-4">Read the full policy</Link>
            </div>
            {error && <p className="mt-4 rounded-xl bg-destructive/10 px-4 py-2.5 text-xs text-destructive" data-testid="checkout-error">{error}</p>}
            <button type="submit" data-testid="place-order-btn" disabled={placing}
              className="mt-5 w-full rounded-full bg-primary py-3.5 text-xs font-medium tracking-[0.2em] text-primary-foreground uppercase transition-colors hover:bg-foreground disabled:opacity-50">
              {placing ? "Placing order..." : `Place order · ${formatINR(cartTotal + (method === "cod" ? 120 : 70))}`}
            </button>
            <p className="mt-3 text-center text-[11px] font-light text-muted-foreground">Guest checkout — no account needed</p>
          </div>
        </Reveal>
      </form>
    </div>
  );
}
